import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CalendarDays, Gift, PackageCheck, RotateCcw } from 'lucide-react';
import { useApiClient } from '../lib/apiClient';

type PromotionSummaryRow = {
  id: string;
  active: boolean;
  redemptionCount: number;
  discountRedeemed: number;
};

type PackageSummaryRow = {
  id: string;
  status: string;
  remainingQuantity: number;
  settlementStatus?: string | null;
};

type PackageEligibilityRow = {
  status: string;
  remainingQuantity: number;
  expiresAt?: string | null;
};

type AppointmentShortcutRow = {
  id: string;
  startTime: string;
  status: 'SCHEDULED' | 'PORTAL_BOOKED' | 'CHECKED_IN' | 'COMPLETED' | 'CANCELLED' | 'NO_SHOW';
  patient: { id?: string; name: string };
  appointmentType?: { id?: string; name?: string | null } | null;
  billingService?: { name?: string | null } | null;
  location?: { name?: string | null } | null;
};

function startOfDay(value: Date) {
  const date = new Date(value);
  date.setHours(0, 0, 0, 0);
  return date;
}

function endOfDay(value: Date) {
  const date = new Date(value);
  date.setHours(23, 59, 59, 999);
  return date;
}

function packageIsEligible(pkg: PackageEligibilityRow) {
  const expiresAt = pkg.expiresAt ? new Date(pkg.expiresAt) : null;
  return pkg.status === 'ACTIVE' && Number(pkg.remainingQuantity || 0) > 0 && (!expiresAt || expiresAt >= new Date());
}

export default function OffersPackagesPage() {
  const { fetchJson } = useApiClient();
  const [promotions, setPromotions] = useState<PromotionSummaryRow[]>([]);
  const [packages, setPackages] = useState<PackageSummaryRow[]>([]);
  const [appointments, setAppointments] = useState<AppointmentShortcutRow[]>([]);
  const [packageEligibilityByAppointment, setPackageEligibilityByAppointment] = useState<Record<string, boolean | null>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  async function load() {
    setLoading(true);
    setError('');
    try {
      const today = new Date();
      const [promotionResult, packageResult, appointmentResult] = await Promise.allSettled([
        fetchJson<{ data: PromotionSummaryRow[] }>('/promotions'),
        fetchJson<{ data: PackageSummaryRow[] }>('/packages'),
        fetchJson<AppointmentShortcutRow[]>(`/appointments?start=${encodeURIComponent(startOfDay(today).toISOString())}&end=${encodeURIComponent(endOfDay(today).toISOString())}`),
      ]);

      if (promotionResult.status === 'rejected') throw promotionResult.reason;
      if (packageResult.status === 'rejected') throw packageResult.reason;

      const promotionPayload = promotionResult.value;
      const packagePayload = packageResult.value;
      setPromotions(promotionPayload.data || []);
      setPackages(packagePayload.data || []);
      setAppointments(
        appointmentResult.status === 'fulfilled'
          ? appointmentResult.value || []
          : [],
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load offers and packages.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  const promotionSummary = useMemo(() => ({
    active: promotions.filter((row) => row.active).length,
    redemptions: promotions.reduce((sum, row) => sum + Number(row.redemptionCount || 0), 0),
    discountRedeemed: promotions.reduce((sum, row) => sum + Number(row.discountRedeemed || 0), 0),
  }), [promotions]);

  const packageSummary = useMemo(() => ({
    active: packages.filter((row) => row.status === 'ACTIVE').length,
    remainingUnits: packages.reduce((sum, row) => sum + Number(row.remainingQuantity || 0), 0),
    depleted: packages.filter((row) => row.status === 'DEPLETED').length,
    unpaid: packages.filter((row) => row.status === 'ACTIVE' && row.settlementStatus && row.settlementStatus !== 'PAID_EXTERNALLY' && row.settlementStatus !== 'COMPLIMENTARY').length,
  }), [packages]);

  const checkoutShortcuts = useMemo(
    () => appointments
      .filter((row) => row.status !== 'CANCELLED' && row.status !== 'NO_SHOW')
      .sort((left, right) => new Date(left.startTime).getTime() - new Date(right.startTime).getTime())
      .slice(0, 6),
    [appointments],
  );

  useEffect(() => {
    let active = true;
    const eligibleCandidates = checkoutShortcuts.filter((appointment) => appointment.patient.id && appointment.appointmentType?.id);
    if (!eligibleCandidates.length) {
      setPackageEligibilityByAppointment({});
      return () => {
        active = false;
      };
    }

    setPackageEligibilityByAppointment(Object.fromEntries(eligibleCandidates.map((appointment) => [appointment.id, null])));

    Promise.all(
      eligibleCandidates.map(async (appointment) => {
        const payload = await fetchJson<{ data: PackageEligibilityRow[] }>(
          `/packages?patientId=${encodeURIComponent(appointment.patient.id || '')}&appointmentTypeId=${encodeURIComponent(appointment.appointmentType?.id || '')}`,
        );
        const hasEligiblePackage = (payload.data || []).some(packageIsEligible);
        return [appointment.id, hasEligiblePackage] as const;
      }),
    )
      .then((results) => {
        if (!active) return;
        setPackageEligibilityByAppointment(Object.fromEntries(results));
      })
      .catch(() => {
        if (!active) return;
        setPackageEligibilityByAppointment(Object.fromEntries(eligibleCandidates.map((appointment) => [appointment.id, false])));
      });

    return () => {
      active = false;
    };
  }, [checkoutShortcuts, fetchJson]);

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Offers &amp; Packages</p>
          <h1 className="text-2xl font-semibold text-slate-900">Discounts and prepaid services</h1>
          <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">Manage offer codes and prepaid package balances. Packages are usually consumed from the visit workspace, while this hub keeps checkout shortcuts and front-desk adjustments close at hand.</p>
        </div>
        <button
          type="button"
          onClick={() => void load()}
          className="inline-flex items-center gap-2 rounded-md border border-slate-300 px-3 py-2 text-sm font-medium"
        >
          <RotateCcw size={16} />
          Refresh
        </button>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition hover:border-slate-300 hover:shadow-md">
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-900"><Gift size={18} /> Offers</div>
          <p className="mt-2 text-sm leading-6 text-slate-600">Create and manage codes used during checkout or invoice editing.</p>
          <div className="mt-4 grid grid-cols-2 gap-3">
            <MetricCard label="Active" value={promotionSummary.active} />
            <MetricCard label="Redeemed" value={promotionSummary.redemptions} />
          </div>
          <div className="mt-4">
            <Link to="/promotions" data-focus="focus=promotion" className="inline-flex min-h-10 items-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
              Manage offers
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition hover:border-slate-300 hover:shadow-md">
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-900"><PackageCheck size={18} /> Packages</div>
          <p className="mt-2 text-sm leading-6 text-slate-600">Register prepaid balances, monitor remaining service units, and send staff to the visit workspace and use Package credit when care is delivered.</p>
          <div className="mt-4 grid grid-cols-2 gap-3">
            <MetricCard label="Active" value={packageSummary.active} />
            <MetricCard label="Units" value={packageSummary.remainingUnits} />
          </div>
          <div className="mt-4">
            <Link to="/packages" data-focus="focus=package" className="inline-flex min-h-10 items-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
              Manage packages
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-900"><CalendarDays size={18} /> Checkout rules</div>
          <div className="mt-3 space-y-3 text-sm leading-6 text-slate-600">
            <p>Use checkout for today's front-desk payment work.</p>
            <p>Use packages only when a matching prepaid balance exists, or register one first.</p>
          </div>
        </div>
      </div>

      {error ? <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div> : null}
      {loading ? <div className="rounded-lg border border-slate-200 bg-white p-5 text-slate-500">Loading offers workspace...</div> : null}

      <section className="rounded-xl border border-slate-200 bg-white p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
              <CalendarDays size={18} />
              Today's appointments
            </div>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600">
              Open checkout for payment work. Package checkout appears when a matching package exists; otherwise register a package for that patient and service.
            </p>
          </div>
          <Link
            to="/appointments"
            className="inline-flex items-center gap-2 rounded-md border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700"
          >
            Open appointments
            <ArrowRight size={16} />
          </Link>
        </div>
        <div className="mt-5 space-y-3">
          {checkoutShortcuts.length ? (
            checkoutShortcuts.map((appointment) => {
              const serviceName = appointment.billingService?.name?.trim() || appointment.appointmentType?.name?.trim() || 'Appointment';
              const startLabel = new Date(appointment.startTime).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
              const packageEligibility = packageEligibilityByAppointment[appointment.id];
              const packageSetupUrl = `/packages?${new URLSearchParams({
                patientId: appointment.patient.id || '',
                appointmentTypeId: appointment.appointmentType?.id || '',
                returnTo: `/appointments/${appointment.id}/payment?focus=package`,
              }).toString()}`;
              return (
                <div key={appointment.id} className="flex flex-col gap-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm lg:flex-row lg:items-center lg:justify-between">
                  <div className="flex items-start gap-4">
                    <div className="min-w-20 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-center text-sm font-semibold text-slate-900">{startLabel}</div>
                    <div>
                      <p className="text-sm font-semibold text-slate-900">{appointment.patient.name}</p>
                      <p className="text-sm text-slate-600">{serviceName}</p>
                      <p className="mt-1 text-xs uppercase tracking-wide text-slate-500">
                        {appointment.location?.name || 'No location recorded'}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Link
                      to={`/appointments/${appointment.id}/payment`}
                      className="inline-flex min-h-10 items-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white"
                    >
                      Open checkout
                    </Link>
                    {packageEligibility === true ? (
                      <Link
                        to={`/appointments/${appointment.id}/payment?focus=package`}
                        className="inline-flex items-center gap-2 rounded-md bg-slate-900 px-3 py-2 text-sm font-medium text-white"
                      >
                        Package checkout
                      </Link>
                    ) : packageEligibility === null ? (
                      <span className="inline-flex min-h-10 items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-medium text-slate-500">
                        Checking packages...
                      </span>
                    ) : (
                      <Link to={packageSetupUrl} className="inline-flex min-h-10 items-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
                        Register package
                      </Link>
                    )}
                  </div>
                </div>
              );
            })
          ) : (
            <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 px-4 py-5 text-sm text-slate-600">
              No active appointments found for today. Use <Link to="/appointments" className="font-medium text-slate-900 underline">Appointments</Link> to pick another visit and open checkout.
            </div>
          )}
        </div>
      </section>

    </div>
  );
}

function MetricCard({ label, value, currency = false }: { label: string; value: number; currency?: boolean }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
      <div className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-2 text-2xl font-semibold text-slate-900">
        {currency ? `$${Number(value || 0).toLocaleString()}` : Number(value || 0).toLocaleString()}
      </div>
    </div>
  );
}
